import {
  createContext,
  useContext,
  PropsWithChildren,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { getSharedAuthClient, AuthClient } from '@appcues/auth';
import {
  setTags,
  setUserId as setUserIdForErrorReporting,
} from 'studio5/lib/error-reporting';
import { postLogout } from 'studio5/lib/crx';

type AuthContextType = {
  accountId?: string;
  client: AuthClient;
  despoof: () => Promise<void>;
  isSpoofing?: boolean;
  logout: () => Promise<void>;
  spoof: (id: string) => Promise<void>;
  switchAccount: (id: string) => Promise<void>;
  token?: string;
  userId?: string;
  getUserAccounts?: () => Promise<unknown[]>;
};

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export default function AuthProvider({ children }: PropsWithChildren<unknown>) {
  const authClient = getSharedAuthClient()!;

  const {
    getAccountId,
    getUserId,
    getToken,
    isSpoofing: getIsSpoofing,
    getUserAccounts,
  } = authClient;

  const [accountId, setAccountId] = useState<string>();
  const [token, setToken] = useState<string>();
  const [userId, setUserId] = useState<string>();
  const [isSpoofing, setSpoofing] = useState<boolean>();

  useEffect(() => {
    getAccountId().then(setAccountId);
  }, [getAccountId]);

  useEffect(() => {
    getUserId().then(setUserId);
  }, [getUserId]);

  useEffect(() => {
    getIsSpoofing().then(setSpoofing);
  }, [getIsSpoofing]);

  useEffect(() => {
    getToken().then(setToken);
  }, [getToken]);

  useEffect(() => {
    setTags({ accountId, userId, isSpoofing });
    setUserIdForErrorReporting(userId);
  }, [accountId, userId, isSpoofing]);

  const switchAccount = useCallback(
    async (id: string) => {
      window.Appcues?.reset();
      const user = await authClient.switchAccount(id);
      return user;
    },
    [authClient]
  );

  const spoof = useCallback(
    (id: string) => {
      window.Appcues?.reset();
      return authClient.spoof(id);
    },
    [authClient]
  );

  const despoof = useCallback(() => {
    window.Appcues?.reset();
    return authClient.despoof();
  }, [authClient]);

  const logout = useCallback(async () => {
    window.Appcues?.reset();
    try {
      // Notify the Chrome extension to invalidate its token before logging
      // out. Wait for delivery (capped by a timeout, since the extension's
      // log-out handler does not respond) so it isn't cancelled by the
      // full-page redirect that Auth0 logout triggers. Done here — the single
      // logout chokepoint — so every entry point (nav, settings, accounts)
      // notifies the extension, not just the /logout route.
      await Promise.race([
        postLogout(),
        new Promise(resolve => {
          setTimeout(resolve, 500);
        }),
      ]);
    } catch {
      // The extension isn't installed (chrome.runtime unavailable), so the
      // message throws. Logout must still proceed regardless.
    }
    return authClient.logout();
  }, [authClient]);

  const contextValue = useMemo(() => {
    return {
      accountId,
      client: authClient,
      token,
      despoof,
      isSpoofing,
      logout,
      spoof,
      switchAccount,
      userId,
      getUserAccounts,
    };
  }, [
    accountId,
    userId,
    isSpoofing,
    token,
    authClient,
    despoof,
    logout,
    spoof,
    switchAccount,
    getUserAccounts,
  ]);

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
